import { Fragment, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Info, Lightbulb, Scale } from "lucide-react";
import { renderInline, type LinkRenderer } from "@/lib/miniMarkdown";
import type { CalloutTone, GuideBlock } from "@/content/guides/body";
import { GUIDE_IMAGES, type GuideImage } from "@/content/guides/images";
import { GuideDiagram } from "./GuideDiagrams";

// Renders a guide's blocks (src/content/guides/body.ts) as React elements, never HTML strings: Markdown through
// src/lib/miniMarkdown, internal links (/path) as router links, plus callouts, figures, diagrams and feature-link
// cards. The article column's typography is the .guide-prose class in src/index.css.

/** Internal paths become router links (client-side navigation); anything else opens normally. */
export const guideLink: LinkRenderer = (href, children, key) =>
  href.startsWith("/") ? (
    <Link key={key} to={href}>
      {children}
    </Link>
  ) : href.startsWith("mailto:") ? (
    <a key={key} href={href}>
      {children}
    </a>
  ) : (
    <a key={key} href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );

const inline = (s: string, key: string) => renderInline(s, guideLink, key);

const CALLOUT: Record<CalloutTone, { icon: typeof Info; label: string; cls: string }> = {
  tip: { icon: Lightbulb, label: "Tip", cls: "guide-callout--tip" },
  note: { icon: Info, label: "Note", cls: "guide-callout--note" },
  warning: { icon: AlertTriangle, label: "Watch out", cls: "guide-callout--warning" },
  legal: { icon: Scale, label: "Not legal advice", cls: "guide-callout--legal" },
};

/** A responsive image for a registered guide image: a <picture> with AVIF and WebP ahead of the JPEG where the image
 *  has them (the content images), else an <img> with its srcset. Always width/height; lazy unless `eager`. */
export const GuideImg = ({ img, alt, eager = false, sizes, className }: { img: GuideImage; alt: string; eager?: boolean; sizes?: string; className?: string }) => {
  const s = sizes ?? img.sizes ?? "(min-width: 768px) 680px, 100vw";
  const el = (
    <img
      src={img.src}
      srcSet={img.srcSet}
      sizes={s}
      width={img.width}
      height={img.height}
      alt={alt}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      // React 18 doesn't know fetchPriority yet; pass it through as the HTML attribute.
      {...(eager ? ({ fetchpriority: "high" } as object) : {})}
      className={className ?? "block h-auto w-full"}
    />
  );
  if (!img.sources?.length) return el;
  return (
    <picture>
      {img.sources.map((src) => (
        <source key={src.type} type={src.type} srcSet={src.srcSet} sizes={s} />
      ))}
      {el}
    </picture>
  );
};

const Frame = ({ frame, children }: { frame: "browser" | "phone" | "none"; children: ReactNode }) => {
  if (frame === "browser") {
    return (
      <div className="overflow-hidden rounded-xl border border-border bg-white shadow-lg">
        <div className="flex items-center gap-1.5 border-b border-black/5 bg-zinc-100 px-3 py-2" aria-hidden="true">
          <span className="h-2.5 w-2.5 rounded-full bg-zinc-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-zinc-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-zinc-300" />
        </div>
        {children}
      </div>
    );
  }
  if (frame === "phone") {
    return <div className="mx-auto w-full max-w-[300px] overflow-hidden rounded-[26px] border-[6px] border-zinc-900 bg-white shadow-xl">{children}</div>;
  }
  return <div className="overflow-hidden rounded-xl border border-border">{children}</div>;
};

export const GuideBlocks = ({ blocks, keyPrefix = "b" }: { blocks: GuideBlock[]; keyPrefix?: string }) => (
  <>
    {blocks.map((b, i) => {
      const key = `${keyPrefix}${i}`;
      switch (b.type) {
        case "heading": {
          const Tag = (b.level === 1 ? "h2" : `h${b.level}`) as "h2" | "h3";
          return (
            <Tag key={key} id={b.id}>
              {inline(b.text, key)}
            </Tag>
          );
        }
        case "paragraph":
          return (
            <p key={key}>
              {b.text.split("\n").map((part, j) => (
                <Fragment key={j}>
                  {j > 0 && <br />}
                  {inline(part, `${key}-${j}`)}
                </Fragment>
              ))}
            </p>
          );
        case "quote":
          return <blockquote key={key}>{inline(b.text, key)}</blockquote>;
        case "rule":
          return <hr key={key} />;
        case "list": {
          const Tag = b.ordered ? "ol" : "ul";
          return (
            <Tag key={key}>
              {b.items.map((it, j) => (
                <li key={j}>{inline(it, `${key}-${j}`)}</li>
              ))}
            </Tag>
          );
        }
        case "table":
          return (
            <div key={key} className="guide-table" role="region" aria-label={b.head.join(", ")} tabIndex={0}>
              <table>
                <thead>
                  <tr>
                    {b.head.map((c, j) => (
                      <th key={j} scope="col">
                        {inline(c, `${key}-h${j}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {b.rows.map((r, j) => (
                    <tr key={j}>
                      {r.map((c, k) =>
                        k === 0 ? (
                          <th key={k} scope="row">
                            {inline(c, `${key}-${j}-${k}`)}
                          </th>
                        ) : (
                          <td key={k}>{inline(c, `${key}-${j}-${k}`)}</td>
                        ),
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        case "callout": {
          const c = CALLOUT[b.tone];
          const Icon = c.icon;
          return (
            <aside key={key} className={`guide-callout ${c.cls}`} aria-label={b.title ?? c.label}>
              <p className="guide-callout__title">
                <Icon size={18} aria-hidden="true" />
                <span>{b.title ?? c.label}</span>
              </p>
              <GuideBlocks blocks={b.blocks} keyPrefix={`${key}-`} />
            </aside>
          );
        }
        case "figure": {
          const img = GUIDE_IMAGES[b.image];
          if (!img) return null;
          return (
            <figure key={key} className={`guide-figure ${b.frame === "phone" ? "guide-figure--phone" : ""}`}>
              <div className="guide-figure__img" style={img.maxWidth ? { maxWidth: img.maxWidth } : undefined}>
                <Frame frame={b.frame}>
                  <GuideImg img={img} alt={b.alt} sizes={b.frame === "phone" ? "300px" : undefined} />
                </Frame>
              </div>
              {(b.caption || b.credit) && (
                <figcaption>
                  {b.caption && inline(b.caption, `${key}-c`)}
                  {b.credit && <span className="guide-figure__credit">{inline(b.credit, `${key}-cr`)}</span>}
                </figcaption>
              )}
            </figure>
          );
        }
        case "diagram":
          return (
            <figure key={key} className="guide-figure guide-figure--diagram">
              <GuideDiagram id={b.id} />
              {b.caption && <figcaption>{inline(b.caption, `${key}-c`)}</figcaption>}
            </figure>
          );
        case "link":
          return (
            <Link key={key} to={b.to} className="guide-feature-link not-prose group">
              <span className="min-w-0 flex-1">
                <span className="guide-feature-link__eyebrow">Learn more</span>
                <span className="guide-feature-link__title">{b.title}</span>
                {b.body && <span className="guide-feature-link__body">{b.body}</span>}
              </span>
              <ArrowRight size={18} aria-hidden="true" className="flex-none text-hawk-orange transition-transform group-hover:translate-x-0.5" />
            </Link>
          );
        default:
          return null;
      }
    })}
  </>
);
